import React, { useEffect, useMemo, useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  IconButton,
  Box,
  useTheme,
  Collapse,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  ExpandLess,
  ExpandMore,
  Home,
  CalendarViewMonth,
  Paid,
  Logout,
  Person,
  ListAlt,
  Search,
} from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import { useLocation, useNavigate } from "react-router-dom";
import handleAxiosError from "./ErrorHandler";
import { notifErr, notifOk } from "./Functions";
import { postFetcher } from "./Fetchers";

interface NavItem {
  id: string;
  text: string;
  path?: string;
  icon: React.ReactNode;
  subitems?: {
    id: string;
    text: string;
    path: string;
    icon: React.ReactNode;
  }[];
}

interface SideNavBarProps {
  title: string;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const SideNavBar = ({ title, open, setOpen }: SideNavBarProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedItem, setExpandedItem] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState("");

  const handleLogout = async () => {
    try {
      const response = await postFetcher(`${process.env.REACT_APP_API_URL}/auth/logout`, null);
      notifOk(response.data);
      navigate("/login");
    } catch (err) {
      notifErr(handleAxiosError(err));
    }
  };

  const navItems: NavItem[] = useMemo(
    () => [
      {
        id: "quick-search",
        text: "Rychlé hledání",
        path: "/quick-search",
        icon: <Search />,
      },
      {
        id: "overview",
        text: "Přehled",
        icon: <Home />,
        subitems: [
          {
            id: "courses-overview",
            text: "Kurzy",
            path: "/overview/courses",
            icon: null,
          },
          {
            id: "members-overview",
            text: "Členové",
            path: "/overview/members",
            icon: null,
          },
          {
            id: "coaches-overview",
            text: "Trenéři",
            path: "/overview/coaches",
            icon: null,
          },
        ],
      },
      {
        id: "training-schedule",
        text: "Tréninkový rozvrh",
        icon: <CalendarViewMonth />,
        subitems: [
          {
            id: "schedule",
            text: "Rozpis",
            path: "/schedule/roster",
            icon: null,
          },
          {
            id: "schedule-training-areas",
            text: "Sportoviště",
            path: "/schedule/training-areas",
            icon: null,
          },
          {
            id: "schedule-settings",
            text: "Nastavení",
            path: "/schedule/settings",
            icon: null,
          },
        ],
      },
      {
        id: "schedule-changes",
        text: "Změny v rozvrhu",
        icon: <EditIcon />,
        subitems: [
          {
            id: "sessions-substitutions",
            text: "Záskoky",
            path: "/schedule/substitutions",
            icon: null,
          },
          {
            id: "days-cancellation",
            text: "Rušení dnů",
            path: "schedule/day-cancellation",
            icon: null,
          },
        ],
      },
      {
        id: "attendance",
        text: "Docházka",
        path: "/schedule/attendance",
        icon: <ListAlt />,
      },
      {
        id: "month-summary",
        text: "Vyúčtování",
        path: "month-summary",
        icon: <Paid />,
      },
      {
        id: "account",
        text: "Účet",
        path: "/account",
        icon: <Person />,
      },
    ],
    []
  );

  useEffect(() => {
    const path = location.pathname;

    for (const item of navItems) {
      if (item.path && path.startsWith(item.path)) {
        setSelectedItem(item.id);
        setExpandedItem("");
        return;
      }

      if (item.subitems) {
        for (const sub of item.subitems) {
          if (sub.path && path.startsWith(sub.path)) {
            setSelectedItem(sub.id);
            setExpandedItem(item.id);
            return;
          }
        }
      }
    }
  }, [location.pathname, navItems]);

  const drawerWidth = open ? "15rem" : "3.5rem";

  return (
    <Box sx={{ display: "flex" }}>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: "hidden",
            background:
              "linear-gradient(180deg, var(--gradientStart), var(--gradientEnd))",
            color: "white",
            "& .MuiListItemIcon-root": {
              color: "white",
            },
            "& .MuiDivider-root": {
              borderColor: "rgba(255, 255, 255, 0.3)",
            },
            "& .MuiIconButton-root": {
              color: "white",
            },
            "& .MuiListItemButton-root": {
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.08)",
              },
            },
          },
        }}
      >
        <Box
          sx={{
            height: "3.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: open ? "space-between" : "center",
            padding: theme.spacing(0, "0.5rem"),
          }}
        >
          {open && (
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{ flexGrow: "0.5rem", p: "1rem" }}
            >
              {title}
            </Typography>
          )}
          <IconButton onClick={() => setOpen(!open)}>
            {open ? <ChevronLeft /> : <ChevronRight />}
          </IconButton>
        </Box>
        <Divider />
        <List>
          {navItems.map((item) => (
            <React.Fragment key={item.id}>
              <ListItem
                disablePadding
                sx={{
                  height: "3rem",
                }}
              >
                <ListItemButton
                  onClick={() => {
                    if (!item.subitems) {
                      setSelectedItem(item.id);
                      navigate(item.path!);
                    } else if (open) {
                      setExpandedItem(item.id === expandedItem ? "" : item.id);
                    } else {
                      setOpen(true);
                      setExpandedItem(item.id);
                    }
                  }}
                  sx={{
                    justifyContent: open ? "initial" : "center",
                    color: "inherit",
                    background:
                      selectedItem === item.id ||
                      (!open &&
                        item.subitems &&
                        item.subitems
                          .map((item) => item.id)
                          .includes(selectedItem))
                        ? "rgb(0,0,0,0.18)"
                        : "inherit",
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? "1.5rem" : "auto",
                      color: "inherit",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>

                  {open && (
                    <>
                      <ListItemText
                        primary={item.text}
                        sx={{
                          whiteSpace: "nowrap",
                        }}
                      />
                      {item.subitems &&
                        (expandedItem === item.id ? (
                          <ExpandLess />
                        ) : (
                          <ExpandMore />
                        ))}
                    </>
                  )}
                </ListItemButton>
              </ListItem>

              {item.subitems && open && (
                <Collapse
                  in={expandedItem === item.id}
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding>
                    {item.subitems.map((subitem) => (
                      <ListItemButton
                        key={subitem.id}
                        onClick={() => {
                          setSelectedItem(subitem.id);
                          navigate(subitem.path!);
                        }}
                        sx={{
                          height: "3rem",
                          pl: "1.6rem",
                          background:
                            selectedItem === subitem.id
                              ? "rgb(0,0,0,0.18)"
                              : "inherit",
                        }}
                      >
                        <ListItemIcon sx={{ color: "inherit" }}>
                          {subitem.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={subitem.text}
                          sx={{
                            whiteSpace: "nowrap",
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>
        <Box sx={{ mt: "auto" }}>
          <Divider />
          <List>
            <ListItem
              disablePadding
              sx={{
                height: "3rem",
              }}
            >
              <ListItemButton
                onClick={handleLogout}
                sx={{
                  justifyContent: open ? "initial" : "center",
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? "1.5rem" : "auto",
                    color: "inherit",
                  }}
                >
                  <Logout />
                </ListItemIcon>
                {open && (
                  <ListItemText
                    primary="Odhlásit se"
                    sx={{
                      whiteSpace: "nowrap",
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </Box>
  );
};

export default SideNavBar;
